import { createOrvalRequestOptions, CurrentKycDtoStatus, getCurrentPortalCustomer } from "@token/shared";
import type { CurrentKycDtoStatus as CurrentKycDtoStatusValue, CustomerDto, JsonNode } from "@token/shared";
import type { ApiClient } from "@/lib/api";
import type { CustomerSessionApi } from "./types";

export type CustomerSessionApiMode = "http" | "mock";

export interface CreateCustomerSessionApiOptions {
  mode?: CustomerSessionApiMode;
  mockKycStatus?: CurrentKycDtoStatusValue;
}

export class ExternalIdNotFoundError extends Error {
  constructor() {
    super("External ID not found for authenticated user.");
    this.name = "ExternalIdNotFoundError";
  }
}

export function createCustomerSessionApi(api: ApiClient, opts: CreateCustomerSessionApiOptions = {}): CustomerSessionApi {
  if (opts.mode === "mock") {
    return new MockCustomerSessionApi(opts.mockKycStatus);
  }
  return new HttpCustomerSessionApi(api);
}

class HttpCustomerSessionApi implements CustomerSessionApi {
  constructor(private readonly api: ApiClient) {}

  async syncAuthenticatedCustomer(): Promise<CustomerDto> {
    try {
      const response = await getCurrentPortalCustomer(createOrvalRequestOptions(this.api));
      return response.data;
    } catch (error) {
      if (isHttpStatus(error, 400) && hasExternalIdMissingMessage(error)) {
        throw new ExternalIdNotFoundError();
      }
      throw error;
    }
  }
}

function isHttpStatus(error: unknown, status: number): boolean {
  return typeof error === "object" && error !== null && (error as { status?: number }).status === status;
}

function hasExternalIdMissingMessage(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const info = (error as { info?: unknown }).info;
  const message = typeof info === "object" && info !== null ? (info as { message?: unknown }).message : info;
  return typeof message === "string" && message.toLowerCase().includes("externalid is missing");
}

class MockCustomerSessionApi implements CustomerSessionApi {
  constructor(private readonly kycStatus: CurrentKycDtoStatusValue = CurrentKycDtoStatus.NOT_STARTED) {}

  async syncAuthenticatedCustomer(): Promise<CustomerDto> {
    await new Promise((resolve) => window.setTimeout(resolve, 120));
    return {
      id: "mock-customer-1",
      firstName: "Portal",
      lastName: "User",
      email: "investor@example.com",
      kyc: {
        status: this.kycStatus,
        kyc: {
          status: this.kycStatus,
          providerMismatches:
            this.kycStatus === CurrentKycDtoStatus.REJECTED
              ? ({
                  Mismatches: [
                    { FieldName: "FirstName", Value: "Portal", KycValue: "Porta" },
                    { FieldName: "BirthDate", Value: "1990-01-01", KycValue: "1991-01-01" },
                  ],
                } as unknown as JsonNode)
              : undefined,
        },
      },
    };
  }
}
